import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import { useSlaPolicy } from "hooks/useSlaPolicies";
import { useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { requestTypeSelectOptions } from "utilities/requestTypes";
import { TICKET_PRIORITY_OPTIONS, TICKET_PRIORITY_LABELS } from "constants/tickets";
import { formatTargetMins } from "utilities/sla";
import { ArrowLeft, Timer, AlertTriangle } from "lucide-react";

/* ── duration <-> minutes helpers (friendly mins/hours/days input) ─────────── */

const UNIT_MINS = { minutes: 1, hours: 60, days: 60 * 24 };

function minsToDuration(mins) {
  if (mins == null) return { value: "", unit: "hours" };
  if (mins % UNIT_MINS.days === 0) return { value: String(mins / UNIT_MINS.days), unit: "days" };
  if (mins % UNIT_MINS.hours === 0) return { value: String(mins / UNIT_MINS.hours), unit: "hours" };
  return { value: String(mins), unit: "minutes" };
}

function durationToMins({ value, unit }) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * (UNIT_MINS[unit] || 1));
}

function blankDraft() {
  return {
    name: "",
    priority: "", // "" = any (null on the wire)
    requestType: "", // "" = any (null on the wire)
    fr: { value: "", unit: "hours" },
    res: { value: "", unit: "days" },
    active: true,
  };
}

function hydrate(p) {
  return {
    name: p.name || "",
    priority: p.match?.priority || "",
    requestType: p.match?.requestType || "",
    fr: minsToDuration(p.firstResponseTargetMins),
    res: minsToDuration(p.resolutionTargetMins),
    active: p.active !== false,
  };
}

/* ── component ─────────────────────────────────────────────────────────────── */

function SlaPolicyEditor() {
  const { policyId } = useParams();
  const isNew = !policyId || policyId === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const authUser = { id: user.id, accessToken: user.accessToken };
  const { toast } = useToast();

  const { policy, loading } = useSlaPolicy(policyId);
  // Admin vocabulary incl. deactivated types (SOUND-785 ride-along): a policy on
  // a since-deactivated type must show that type, not silently read "Any type".
  const { requestTypes: adminRequestTypes } = useRequestTypesAdmin({ includeInactive: true });

  const [draft, setDraft] = useState(blankDraft);
  const [saving, setSaving] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!isNew && policy && !hydratedRef.current) {
      setDraft(hydrate(policy));
      hydratedRef.current = true;
    }
  }, [isNew, policy]);

  const frMins = durationToMins(draft.fr);
  const resMins = durationToMins(draft.res);

  const issues = useMemo(() => {
    const out = [];
    if (!draft.name.trim()) out.push("Policy needs a name.");
    if (frMins == null) out.push("First-response target must be a positive duration.");
    if (resMins == null) out.push("Resolution target must be a positive duration.");
    return out;
  }, [draft.name, frMins, resMins]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    if (issues.length > 0) {
      setShowIssues(true);
      toast({ status: "warning", title: "Fix the highlighted fields first" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        match: {
          priority: draft.priority || null,
          requestType: draft.requestType || null,
        },
        firstResponseTargetMins: frMins,
        resolutionTargetMins: resMins,
        active: draft.active,
      };
      let saved;
      if (isNew) {
        const res = await makeApiRequest("post", apiRoutes.createSlaPolicy, authUser, companyId, body);
        saved = res.data?.data;
      } else {
        const res = await makeApiRequest("put", apiRoutes.updateSlaPolicy(policyId), authUser, companyId, body);
        saved = res.data?.data;
      }
      setShowIssues(false);
      toast({ status: "success", title: isNew ? "Policy created" : "Policy saved" });
      if (isNew && saved?.policyId) {
        hydratedRef.current = false;
        navigate(`/settings/sla-policies/${saved.policyId}`, { replace: true });
      } else if (saved) {
        setDraft(hydrate(saved));
      }
    } catch (err) {
      toast({
        status: "destructive",
        title: isNew ? "Create failed" : "Save failed",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setSaving(true);
    try {
      const res = await makeApiRequest("delete", apiRoutes.deleteSlaPolicy(policyId), authUser, companyId);
      const updated = res.data?.data;
      if (updated) setDraft(hydrate(updated));
      else set({ active: false });
      toast({ status: "success", title: "Policy deactivated" });
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to deactivate",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  const leave = () => navigate("/settings/sla-policies");

  if (!isNew && loading) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-64" />} />
        <div className="container space-y-6 py-6">
          <Skeleton.Rect className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        sticky
        title={
          <span className="inline-flex items-center gap-2">
            <Timer className="h-5 w-5 text-brand" />
            {isNew ? "New SLA policy" : draft.name || "SLA policy"}
          </span>
        }
        badge={
          !isNew ? (
            <span className={`badge ${draft.active ? "badge-success" : "badge-muted"}`}>
              {draft.active ? "Active" : "Inactive"}
            </span>
          ) : null
        }
        subtitle={
          <button
            onClick={leave}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            SLA Policies
          </button>
        }
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost" disabled={saving} onClick={leave}>
              Cancel
            </button>
            {!isNew && draft.active && (
              <button className="btn btn-outline" disabled={saving} onClick={() => setDeactivateConfirm(true)}>
                Deactivate
              </button>
            )}
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {isNew ? "Create policy" : "Save"}
            </button>
          </div>
        }
      />

      <div className="container max-w-3xl space-y-6 py-6">
        {showIssues && issues.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Resolve before saving
            </div>
            <ul className="list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
              {issues.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Identity */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium text-foreground">Name</span>
            <input
              className="input"
              placeholder="e.g. Urgent tickets — 30m / 8h"
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </label>
        </section>

        {/* Match */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h2 className="mb-1 text-lg font-semibold">Applies to</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Leave both on <span className="font-medium">Any</span> for the global default. The most specific
            active policy matching a ticket's priority and request type wins.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Priority</span>
              <select className="select" value={draft.priority} onChange={(e) => set({ priority: e.target.value })}>
                <option value="">Any priority</option>
                {TICKET_PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {TICKET_PRIORITY_LABELS[p] || p}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Request type</span>
              <select className="select" value={draft.requestType} onChange={(e) => set({ requestType: e.target.value })}>
                <option value="">Any type</option>
                {requestTypeSelectOptions(adminRequestTypes, draft.requestType).map((rt) => (
                  <option key={rt.key} value={rt.key} disabled={!rt.active}>
                    {rt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {/* Targets */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h2 className="mb-1 text-lg font-semibold">Targets</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Measured on a 24×7 clock; paused while a ticket is waiting on the client.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DurationField
              label="First response"
              value={draft.fr}
              mins={frMins}
              onChange={(fr) => set({ fr })}
            />
            <DurationField
              label="Resolution"
              value={draft.res}
              mins={resMins}
              onChange={(res) => set({ res })}
            />
          </div>
        </section>

        {/* Active */}
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={draft.active}
              onChange={(e) => set({ active: e.target.checked })}
            />
            <span>
              <span className="font-medium text-foreground">Active</span>
              <span className="ml-2 text-muted-foreground">
                Inactive policies stop matching new tickets but stay resolvable for history.
              </span>
            </span>
          </label>
        </section>
      </div>

      <ConfirmDialog
        open={deactivateConfirm}
        title="Deactivate this policy?"
        description="It will stop matching new tickets. Already-stamped tickets keep their due dates. You can reactivate it later."
        confirmLabel="Deactivate"
        variant="destructive"
        onCancel={() => setDeactivateConfirm(false)}
        onConfirm={() => {
          setDeactivateConfirm(false);
          deactivate();
        }}
      />
    </div>
  );
}

function DurationField({ label, value, mins, onChange }) {
  return (
    <div className="flex flex-col text-sm">
      <span className="mb-1 font-medium text-foreground">{label}</span>
      <div className="flex gap-2">
        <input
          type="number"
          min="1"
          className="input w-24"
          placeholder="0"
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
        <select
          className="select w-auto"
          value={value.unit}
          onChange={(e) => onChange({ ...value, unit: e.target.value })}
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
      </div>
      {mins != null && (
        <span className="mt-1 text-xs text-muted-foreground">= {formatTargetMins(mins)} ({mins.toLocaleString()} min)</span>
      )}
    </div>
  );
}

export default requireAuth(SlaPolicyEditor);
